import React from 'react';
import DoughnutGauge from '@/areas/dashboard/components/DoughnutGauge';
import DashboardCard from '@/areas/dashboard/components/DashboardCard';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import '@/areas/dashboard/dashboard.css';
import DoughnutChart from '@/areas/dashboard/components/DoughnutChart';
import { useApiPortalV1AssessmentsResultsListQuery } from '@/areas/assessment-schedule/api';
import styled from 'styled-components';
import { BulletListFormatter } from '@/common/utils/AIUtils';
import { BodyText, PageSection } from '@/common/styles/page';
import { Divider } from 'semantic-ui-react';


import { PillarData } from '@/areas/culture-hub/types/api';

import { AggregateType } from '@/common/constants/AggregateType';
interface ScoreGaugeCardProps {
  overallScore: number;
  differential: number | null;
  previousScore: number | null;
  aiSummary?: { summary?: string; bullets?: string[] } | null;

  assessmentPeriodId?: number;
  teamId?: number;
  teamName?: string | null;
  employeeId?: number;

  // optional: supply pillars from the analytics query so themes update when team/period change
  pillars?: PillarData[];
}

interface SentimentScore {
  id: number;
  label: string;
  caption: string;
  value: number;
  color: string;
}

const VERY_POSITIVE = 0;
const SOMEWHAT_POSITIVE = 1;
const NEUTRAL = 2;
const SOMEWHAT_NEGATIVE = 3;
const VERY_NEGATIVE = 4;

export const ScoreGaugeCard: React.FC<ScoreGaugeCardProps> = ({
  overallScore,
  differential,
  previousScore,
  aiSummary,
  assessmentPeriodId,
  teamId,
  teamName,
  employeeId,
  pillars: pillarsProp,
}) => {

  const getSummaryText = (): string | null => aiSummary?.summary || null;

  const summaryText = getSummaryText();
  const hasPreviousPeriod = previousScore !== null && differential !== null;
  // Compute displayed change from rounded values to avoid small rounding noise
  const displayedDiff = hasPreviousPeriod ? (Number(overallScore.toFixed(1)) - Number((previousScore as number).toFixed(1))) : null;
  // Use pillars passed in as a prop; if not provided, fall back to empty array.
  const sourcePillars = React.useMemo(() => (Array.isArray(pillarsProp) ? pillarsProp : []), [pillarsProp]);

  // For each pillar, pick the single theme with the highest sentiment "strength" (0..1).
  const topThemesByPillar = React.useMemo(() => {
    if (!Array.isArray(sourcePillars) || sourcePillars.length === 0) return [];

    return sourcePillars
      .map((p) => {
        const themes = Array.isArray(p.themes) ? p.themes : [];
        if (themes.length === 0) return null;
        // pick theme with max sentiment (fall back to 0)
        let best = themes[0];
        let bestScore = Math.max(0, Math.min(1, Number((best as any).sentiment ?? 0)));
        for (let i = 1; i < themes.length; i += 1) {
          const t = themes[i];
          const s = Math.max(0, Math.min(1, Number((t as any).sentiment ?? 0)));
          if (s > bestScore) {
            best = t;
            bestScore = s;
          }
        }
        return { pillarKey: p.key, pillarName: p.name, theme: best, strength: bestScore };
      })
      .filter(Boolean)
      .slice(0, 6); // show up to 6 (one per pillar)
  }, [sourcePillars]);

  const { data: sentimentData, isLoading: sentimentLoading, error: sentimentError } = useApiPortalV1AssessmentsResultsListQuery(
    { aggregateType: AggregateType.TEAM_SUMMARY, assessmentPeriodId: assessmentPeriodId!, employeeId },
    { skip: !assessmentPeriodId }
  );

  const [sentimentScores, setSentimentScores] = React.useState<SentimentScore[]>([]);

  React.useEffect(() => {
    if (sentimentData && sentimentData.length > 0) {
      const result = sentimentData[0]?.result || {};

      // Prefer team distribution when teamId is present
      let distribution = result?.org_distribution?.distribution;

      if (typeof teamId !== 'undefined' && Array.isArray(result?.team_distributions)) {
        const found = result.team_distributions.find((td: any) => Number(td.team_id) === Number(teamId));
        if (found && Array.isArray(found.distribution)) {
          distribution = found.distribution;
        }
      }

      let scores: SentimentScore[] = [];

      if (Array.isArray(distribution) && distribution.length > 0) {
        const map = distribution.reduce<Record<string, number>>((acc, cur) => {
          acc[cur.bucket] = Number(cur.percentage) || 0;
          return acc;
        }, {});

        scores = [
          {
            id: VERY_NEGATIVE,
            label: 'Very Negative',
            caption: 'Dissatisfied and disengaged',
            value: map['Very Negative'] ?? 0,
            color: 'rgba(255, 50, 90, .8)'
          },
          {
            id: SOMEWHAT_NEGATIVE,
            label: 'Somewhat Negative',
            caption: 'Concerned, wants improvement',
            value: map['Negative'] ?? 0,
            color: 'rgba(255, 50, 90, .4)'
          },
          {
            id: NEUTRAL,
            label: 'Neutral',
            caption: 'Mixed or indifferent feelings',
            value: map['Neutral'] ?? 0,
            color: 'rgba(255, 216, 111, 1)'
          },
          {
            id: SOMEWHAT_POSITIVE,
            label: 'Somewhat Positive',
            caption: 'Satisfied, room to grow',
            value: map['Positive'] ?? 0,
            color: 'rgba(129, 199, 132, .5)'
          },
          {
            id: VERY_POSITIVE,
            label: 'Very Positive',
            caption: 'Thriving and highly engaged',
            value: map['Very Positive'] ?? 0,
            color: 'rgba(129, 199, 132, 1)'
          }
        ];
      }

      setSentimentScores(scores);
    } else {
      setSentimentScores([]);
    }
  }, [sentimentData, teamId]);


  return (
    <DashboardCard title="Cultural Assessment" height="100%">
      <PageSection display='flex' flexDirection='column' padleft='20px' padright='20px'>
        <PageSection display='flex' flexDirection='row' gap='70px'>
          <div className="gauge-wrapper">
            <DoughnutGauge max={10} complete={overallScore} precision={1} size={'medium'} />
            {hasPreviousPeriod && displayedDiff !== null && (
              <div
                className={cn(
                  'trend-badge',
                  displayedDiff > 0 ? 'trend-badge-positive' :
                    displayedDiff < 0 ? 'trend-badge-negative' :
                      'trend-badge-neutral'
                )}
              >
                {displayedDiff > 0 ? (
                  <TrendingUp size={14} style={{ marginRight: '2px' }} />
                ) : displayedDiff < 0 ? (
                  <TrendingDown size={14} style={{ marginRight: '2px' }} />
                ) : (
                  <Minus size={14} style={{ marginRight: '2px' }} />
                )}
                {displayedDiff > 0 ? `+${displayedDiff.toFixed(1)}` : displayedDiff.toFixed(1)}
              </div>
            )}
          </div>

          <div className="metric-container metric-column">
            <div className="metric-item">
              <div className="metric-label">Current Score</div>
              <div className="metric-value font-semibold">{overallScore.toFixed(1)}</div>
            </div>

            <div className="metric-item">
              <div className="metric-label ">Previous Score</div>
              <div className="metric-value font-semibold">{hasPreviousPeriod ? (previousScore as number).toFixed(1) : 'N/A'}</div>
            </div>

            <div className="metric-item">
              <div className="metric-label">Change</div>
              <div className={cn(
                'metric-value font-semibold',
                (displayedDiff !== null) && displayedDiff > 0 ? 'metric-value-positive' :
                  (displayedDiff !== null) && displayedDiff < 0 ? 'metric-value-negative' :
                    'metric-value-sm font-semibold'
              )}>
                {hasPreviousPeriod && displayedDiff !== null ? (displayedDiff > 0 ? `+${displayedDiff.toFixed(1)}` : displayedDiff.toFixed(1)) : 'N/A'}
              </div>
            </div>
          </div>
        </PageSection>


        {summaryText && (
          <>
            <Divider />
            <PageSection>
              <div className="narrative-title">{(teamName ?? 'Team') + ' Overview'}</div>
              <BodyText>{aiSummary?.summary}</BodyText>
              <BodyText>{BulletListFormatter(aiSummary?.bullets ? aiSummary?.bullets : [])}</BodyText>

            </PageSection>
          </>
        )}

        {sentimentScores && sentimentScores.length > 0 && (
          <>
            <Divider />
            <PageSection>
              <div className="narrative-title">Feedback Themes</div>
              <div className="narrative-text p-0">
                <div className="flex items-start gap-8">
                  <div style={{ flex: '0 0 140px', width: 140 }}>
                    <DoughnutChart chartData={sentimentScores} size="medium" tooltip="short" />
                  </div>
                  <div className="flex-1">
                    {sentimentScores.map((item, idx) => (
                      <div key={idx} className="flex items-start justify-between gap-3 py-1">
                        <div className="flex items-start gap-3">
                          <LegendBox color={item.color} />
                          <div className="text-xs font-medium">{item.label}</div>
                        </div>
                      </div>
                    ))}

                  </div>
                </div>
                {topThemesByPillar.length > 0 && (
                  <div className="mt-4">
                    <div className="pillar-themes-text scorecard-themes-text" title={topThemesByPillar.map(e => '• ' + ((e as any).theme as any).label).join('\n')}>
                      {topThemesByPillar.map(e => ((e as any).theme as any).label).join(' • ')}
                    </div>
                  </div>
                )}
              </div>
            </PageSection>
          </>)}
      </PageSection >
    </DashboardCard >
  );
};

const LegendBox = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  background-color: ${props => props.color};
  border-radius: 3px;
`;



